<template>
  <div :class="['flex h-full', mobile ? 'w-full' : '']" :style="mobile ? 'width: 100vw; max-width: 100vw;' : ''">
    <div
      :class="[
        'flex-col bg-surface text-surface-foreground overflow-y-auto',
        mobile ? 'w-full h-full' : 'w-full lg:w-auto',
      ]"
      :style="
        mobile
          ? 'width: 100vw; max-width: 100vw; height: 100vh; max-height: 100vh;'
          : 'max-height: calc(100vh - var(--header-height) - 3rem)'
      ">
      <div
        v-if="parentLabel"
        class="flex w-full p-6 gap-1 border-b font-semibold lg:hidden items-center"
        :style="mobile ? 'width: 100vw; max-width: 100vw; box-sizing: border-box;' : ''"
        @click="emit('close')">
        <ChevronLeftIcon class="size-4" />
        {{ $t("Back to {label}", { label: $t(parentLabel) }) }}
      </div>
      <div
        :class="['flex flex-col gap-6 p-8 bg-surface', mobile ? 'w-full h-full' : 'w-full h-full lg:w-[370px]']"
        :style="mobile ? 'width: 100vw; max-width: 100vw; box-sizing: border-box;' : ''">
        <template v-if="items?.length > 0">
          <template v-for="item in items">
            <!-- Desktop Version -->
            <RouterLink
              v-if="!mobile && item.items?.length && !item.button"
              :to="item.to || '/'"
              class="flex justify-between items-center lg:px-4 lg:py-2 lg:hover :bg-surface-focus transition-all rounded-xl h-[44px] cursor-pointer"
              :class="{ 'lg:bg-surface-focus': activeItem?.label == item.label }"
              @mouseenter="!mobile && openSubmenuOnHover(item)"
              @mouseleave="cancelHover"
              @click="
                (e) => {
                  if (item.to && item.to !== '/') {
                    /* Navigate - RouterLink handles this */
                  } else {
                    e.preventDefault();
                    activeItem = item;
                  }
                }
              ">
              <div class="flex gap-3 items-center w-full">
                <component :is="item.icon" v-if="item.icon" />
                <h3 class="font-semibold wrap-break-word">
                  {{ $t(item.label) }}
                </h3>
                <ChevronRightIcon class="size-3.5 transition-all ml-auto" />
              </div>
            </RouterLink>
            <!-- Mobile Version -->
            <div
              v-if="mobile && item.items?.length && !item.button"
              :to="item.to"
              class="flex justify-between items-center lg:px-4 lg:py-2 lg:hover:bg-surface-focus transition-all rounded-xl h-[44px] cursor-pointer"
              :class="{ 'lg:bg-surface-focus': activeItem?.label == item.label }"
              @mouseenter="!mobile && (activeItem = item)"
              @click="activeItem = item">
              <div class="flex gap-3 items-center w-full">
                <component :is="item.icon" v-if="item.icon" />
                <h3 class="font-semibold truncate">
                  {{ $t(item.label) }}
                </h3>
                <ChevronRightIcon class="size-3.5 transition-all ml-auto" />
              </div>
            </div>
            <RouterLink
              v-else-if="!item.items?.length && !item.button"
              :to="item.to || '/'"
              class="flex justify-between items-center lg:px-4 lg:py-2 transition-all lg:hover:bg-surface-focus rounded-xl h-[44px] cursor-pointer"
              @mouseenter="!mobile && (activeItem = item)"
              @click="activeItem = item">
              <div class="flex gap-3 items-center">
                <component :is="item.icon" v-if="item.icon" />
                <CountryFlag v-if="item.region" :code="item.region" />
                <h3 class="font-semibold truncate">
                  {{ $t(item.label) }}
                </h3>
              </div>
            </RouterLink>
            <RouterLink v-else-if="item.to && item.button" :to="item.to">
              <Button
                class="w-full truncate items-center text-call-to-action-foreground h-[44px] gap-1"
                style="background: var(--color-call-to-action) !important"
                >{{ item.label }}<ChevronRightIcon class="size-4"
              /></Button>
            </RouterLink>
          </template>
        </template>
      </div>
    </div>
    <template v-if="items?.some((x) => x.items?.length)">
      <div
        v-if="!mobile"
        class="w-[370px] border-l-border border-l overflow-x-hidden overflow-y-auto"
        style="max-height: calc(100vh - var(--header-height) - 3rem)">
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="-translate-x-1 opacity-0"
          enter-to-class="translate-x-0 opacity-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="translate-x-0 opacity-100"
          leave-to-class="translate-x-1 opacity-0"
          mode="out-in">
          <div :key="activeItem?.label" class="flex flex-col p-6 gap-5">
            <template v-for="(item, index) in activeItem?.items || []">
              <!-- Separator: placed before the first button, after all regular items -->
              <div
                v-if="item.button && index > 0 && !activeItem?.items?.[index - 1]?.button"
                class="bg-border my-2"
                style="height: 1px"></div>
              <!-- Render regular items (tournaments/events) first -->
              <div v-if="!item.button" class="flex flex-col gap-4">
                <RouterLink v-if="item.to" :to="item.to" class="font-semibold wrap-break-word">{{
                  $t(item.label)
                }}</RouterLink>
                <div v-else class="font-semibold truncate">{{ $t(item.label) }}</div>
                <div v-if="item.items?.length" class="flex flex-col gap-6">
                  <RouterLink
                    v-for="subItem in item.items"
                    :to="subItem.to"
                    class="text-muted-foreground truncate hover:underline flex items-center gap-2">
                    <component :is="subItem.icon" v-if="subItem.icon" class="size-4" />
                    {{ $t(subItem.label) }}
                  </RouterLink>
                </div>
                <RouterLink
                  v-if="item.to && item.additionalLabel"
                  :to="item.to"
                  class="text-black text-muted-foreground flex items-center gap-3 mt-2 font-semibold"
                  >{{ $t(item.additionalLabel) }} <ArrowRightIcon class="w-3"
                /></RouterLink>
              </div>
              <!-- Render buttons with black styling after regular items -->
              <RouterLink
                v-else-if="item.button && item.to"
                :to="item.to"
                class="text-black font-semibold flex items-center gap-3 mt-2">
                {{ $t(item.label) }} <ArrowRightIcon class="w-3" />
              </RouterLink>
            </template>
          </div>
        </transition>
      </div>
      <template v-else>
        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="translate-x-5 opacity-0"
          enter-to-class="translate-x-0 opacity-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="translate-x-0 opacity-100"
          leave-to-class="translate-x-5 opacity-0">
          <template v-if="activeItem">
            <Teleport to="body">
              <div
                class="flex flex-col fixed overflow-x-hidden overflow-y-auto bg-surface z-[60]"
                style="
                  left: 0 !important;
                  top: 0 !important;
                  right: 0 !important;
                  bottom: 0 !important;
                  width: 100vw !important;
                  height: 100vh !important;
                  max-width: 100vw !important;
                  margin: 0 !important;
                  padding: 0 !important;
                ">
                <div
                  class="flex items-center gap-1 w-full p-6 border-b font-semibold lg:hidden bg-surface"
                  style="width: 100vw; max-width: 100vw; box-sizing: border-box"
                  @click="activeItem = undefined">
                  <ChevronLeftIcon class="size-4" />
                  {{ $t("Back to {label}", { label: $t(activeItem.label) }) }}
                </div>
                <div class="flex flex-col p-6 gap-6" style="width: 100vw; max-width: 100vw; box-sizing: border-box">
                  <template v-for="(item, index) in activeItem?.items || []">
                    <!-- Separator: placed before the first button, after all regular items -->
                    <div
                      v-if="item.button && index > 0 && !activeItem?.items?.[index - 1]?.button"
                      class="bg-border my-2"
                      style="height: 1px"></div>
                    <!-- Render regular items (tournaments/events) first -->
                    <div v-if="!item.button" class="flex flex-col gap-4">
                      <RouterLink
                        v-if="item.to && !item.additionalLabel"
                        :to="item.to"
                        class="font-semibold truncate"
                        >{{ $t(item.label) }}</RouterLink
                      >
                      <div v-else-if="!item.additionalLabel" class="font-semibold truncate">{{ $t(item.label) }}</div>
                      <div v-if="item.items?.length" class="flex flex-col gap-2">
                        <RouterLink
                          v-for="subItem in item.items"
                          :to="subItem.to"
                          class="text-muted-foreground truncate flex items-center gap-2">
                          <component :is="subItem.icon" v-if="subItem.icon" class="size-4" />
                          {{ $t(subItem.label) }}
                        </RouterLink>
                      </div>
                      <RouterLink
                        v-if="item.to && item.additionalLabel"
                        :to="item.to"
                        class="text-black text-muted-foreground flex items-center gap-3 font-semibold"
                        >{{ $t(item.additionalLabel) }} <ArrowRightIcon class="w-3"
                      /></RouterLink>
                    </div>
                    <!-- Render buttons with black styling after regular items -->
                    <RouterLink
                      v-else-if="item.button && item.to"
                      :to="item.to"
                      class="text-black font-semibold flex items-center gap-3 mt-2">
                      {{ $t(item.label) }} <ArrowRightIcon class="w-3" />
                    </RouterLink>
                  </template>
                </div>
              </div>
            </Teleport>
          </template>
        </transition>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, h, watch } from "vue";
import CountryFlag from "@/components/CountryFlag.vue";
import { ChevronRightIcon, ChevronLeftIcon, ArrowRightIcon, TrophyIcon, GlobeAltIcon } from "@heroicons/vue/24/outline";
import { Button } from "@xs2event/js-lib/components";
import { isoCountry2, SportType } from "@xs2event/js-lib";
import { useI18n } from "vue-i18n";
import { useEvents, useTeams, type TeamResponse } from "@xs2event/js-lib/api";
import { slugify } from "@/helpers/format";
import { CUSTOM_ISO_COUNTRIES } from "@/constants/menuItems";
import { eventsWithTeam, eventsWithSportType, eventsWithCountry, eventsWithTournament } from "@/constants/routes";

const props = defineProps<{ item: unknown; mobile?: boolean; parentLabel?: string }>();

const { locale, t } = useI18n();

// FIXME: Add Enums
const countriesCustomOrder = ["ESP", "GBR", "ITA", "DEU", "FRA", "NLD", "PRT", "USA", "AUT"]; // ISO 3-letter codes
const popularTeamsCustomOrder = {
  GBR: ["manchester-united"],
  ITA: ["ac-milan", "internazionale", "as-roma", "lazio-roma", "como-1907"],
};

const uniqueCountries = new Set<string>(
  props.item?.items?.[0].items
    ?.map((x) => x.region)
    .sort((a, b) => {
      if (a === "WORLD") return 1;
      if (b === "WORLD") return -1;

      const indexA = countriesCustomOrder.indexOf(a);
      const indexB = countriesCustomOrder.indexOf(b);

      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    })
);

// Delay submenu on hover
let hoverTimeout: ReturnType<typeof setTimeout> | null = null;
let lockTimeout: ReturnType<typeof setTimeout> | null = null;
let locked: boolean = false;
let intendedItem = null;

// Called on hover
const openSubmenuOnHover = (item) => {
  // If locked and the item is not the same, ignore
  if (locked && item !== intendedItem) return;

  // Clear previous timeout if user hovers again before delay ends
  if (hoverTimeout) clearTimeout(hoverTimeout);
  if (lockTimeout) clearTimeout(lockTimeout);

  // Lock temporarily while waiting for confirmation
  locked = true;
  intendedItem = item;

  hoverTimeout = setTimeout(() => {
    activeItem.value = item;
    // Short grace period to allow switching
    lockTimeout = setTimeout(() => {
      locked = false;
      intendedItem = null;
    }, 150); // unlock slightly after submenu shows
  }, 150); // submenu show delay
};

const cancelHover = (): void => {
  if (hoverTimeout) clearTimeout(hoverTimeout);
  if (lockTimeout) clearTimeout(lockTimeout);
  locked = false;
  intendedItem = null;
};

const teamsQuery = useTeams(
  computed(() => ({ isoCountry: `in:[${Array.from(uniqueCountries).join(",")}]`, sportType: "soccer", popular: true })),
  {
    enabled: props.item.label == "Football",
    staleTime: 1000 * 60 * 60 * 24, //A day
  }
);

const noPopularTeamsCountries = computed(() => {
  // Return countries that do NOT have any popular teams
  if (!teamsQuery.data.value?.teams) return [];
  return Array.from(uniqueCountries).filter((country) => {
    return !teamsQuery.data.value?.teams.some((team) => team.isoCountry === country);
  });
});

const allTeamsQuery = useTeams(
  computed(() => ({
    isoCountry: `in:[${noPopularTeamsCountries.value.join(",")}]`,
    sportType: "soccer",
    popular: false,
    pageSize: 300,
    eventStartdate: `ge:${new Date().toISOString().split("T")[0]}`,
  })),
  {
    enabled: computed(() => props.item.label == "Football" && noPopularTeamsCountries.value?.length > 0),
    staleTime: 1000 * 60 * 60 * 3,
  }
);

// Query to get all teams (both popular and non-popular) for countries that have popular teams
const allTeamsForPopularCountriesQuery = useTeams(
  computed(() => ({
    isoCountry: `in:[${Array.from(uniqueCountries)
      .filter((c) => !noPopularTeamsCountries.value.includes(c))
      .join(",")}]`,
    sportType: "soccer",
    pageSize: 300,
    eventStartdate: `ge:${new Date().toISOString().split("T")[0]}`,
  })),
  {
    enabled: computed(
      () =>
        props.item.label == "Football" &&
        Array.from(uniqueCountries).filter((c) => !noPopularTeamsCountries.value.includes(c)).length > 0
    ),
    staleTime: 1000 * 60 * 60 * 3,
  }
);

// INFO: Fetch events to check if the teams have any event linked on them as home team
const eventsQuery = useEvents(
  computed(() => ({
    sportType: SportType.FOOTBALL,
    country: `in:[${noPopularTeamsCountries.value.join(",")}]`,
    dateStart: `ge:${new Date().toISOString().split("T")[0]}`,
    pageSize: 9999,
    ticketsAvailable: "ge:1",
  })),
  {
    enabled: computed(() => props.item.label == "Football" && noPopularTeamsCountries.value.length > 0),
    staleTime: 1000 * 60 * 60 * 3,
  }
);

// INFO: Fetch events for countries WITH popular teams to filter by home teams only
const eventsForPopularCountriesQuery = useEvents(
  computed(() => ({
    sportType: SportType.FOOTBALL,
    country: `in:[${Array.from(uniqueCountries)
      .filter((c) => !noPopularTeamsCountries.value.includes(c))
      .join(",")}]`,
    dateStart: `ge:${new Date().toISOString().split("T")[0]}`,
    pageSize: 9999,
    ticketsAvailable: "ge:1",
  })),
  {
    enabled: computed(
      () =>
        props.item.label == "Football" &&
        Array.from(uniqueCountries).filter((c) => !noPopularTeamsCountries.value.includes(c)).length > 0
    ),
    staleTime: 1000 * 60 * 60 * 3,
  }
);

const teamsWithEvents = computed(() => {
  if (!eventsQuery.data.value?.events || !allTeamsQuery.data.value?.teams) return [];

  // Get all team IDs from events (only home teams)
  const eventTeamIds = new Set<string>();
  eventsQuery.data.value.events.forEach((event) => {
    if (event.hometeamId) eventTeamIds.add(event.hometeamId);
  });

  // Return only teams that have at least an event as a home team
  return allTeamsQuery.data.value.teams.filter((team) => eventTeamIds.has(team.teamId));
});

const teamsWithEventsForPopularCountries = computed(() => {
  if (!eventsForPopularCountriesQuery.data.value?.events || !allTeamsForPopularCountriesQuery.data.value?.teams)
    return [];

  // Get all team IDs from events (only home teams)
  const eventTeamIds = new Set<string>();
  eventsForPopularCountriesQuery.data.value.events.forEach((event) => {
    if (event.hometeamId) eventTeamIds.add(event.hometeamId);
  });

  // Return only teams that have at least an event as a home team
  return allTeamsForPopularCountriesQuery.data.value.teams.filter((team) => eventTeamIds.has(team.teamId));
});

const countries = computed(() => {
  return Object.fromEntries(
    Array.from(uniqueCountries).map((key) => {
      return [
        key,
        {
          allTeams: teamsWithEvents.value.filter((x) => x.isoCountry == key) || [],
          popularTeams: getSortedTeams(
            teamsQuery.data.value?.teams.filter((x) => x.isoCountry == key) || [],
            popularTeamsCustomOrder
          ),
          popularTournaments: props.item.items[0].items.filter((x) => x.region == key),
        },
      ];
    })
  );
});

const getSortedTeams = (teams: TeamResponse[], orderConfig: Record<string, string[]>, limit?: number) => {
  if (!teams || teams.length === 0) return [];
  // Find the country code for these teams (assume all teams have the same isoCountry)
  const country = teams[0]?.isoCountry || teams[0]?.iso_country;
  const customOrder = country && orderConfig[country];

  let sorted: TeamResponse[] = [];

  if (customOrder && customOrder.length > 0) {
    // Teams in custom order, in the order specified
    const ordered = customOrder
      .map((slug) => teams.find((team) => team.slug === slug))
      .filter(Boolean) as TeamResponse[];

    // Teams not in custom order, sorted alphabetically
    const rest = teams
      .filter((team) => !customOrder.includes(team.slug))
      .sort((a, b) => a.officialName.localeCompare(b.officialName));
    sorted = [...ordered, ...rest];
  } else {
    // No custom order, sort all alphabetically
    sorted = teams.slice().sort((a, b) => a.officialName.localeCompare(b.officialName));
  }

  if (limit) {
    return sorted.slice(0, limit);
  }

  return sorted;
};

// Other tournaments and leagues link based on the other tournaments
const buildWorldTournamentLink = (tournaments) => {
  const tournamentIds = [
    tournaments
      .map((tournament) => {
        return tournament?.to?.query?.tournament;
      })
      .filter(Boolean),
  ];
  return tournamentIds.length
    ? eventsWithTournament(tournamentIds.join(","), { sport_type: "football" })
    : eventsWithSportType(SportType.FOOTBALL);
};

const items = computed(() => {
  let _items = [];

  if (props.item.label === "Other events") {
    // Map each sport: tournaments first, then buttons at the end
    const mapped = props.item.items.map((item) => {
      const sportType = item.params?.sport_type || item.label?.toLowerCase().replace(/\s/g, "");

      // Separate buttons from regular items
      const regularItems = (item.items || []).filter((i) => !i.button);
      const existingButtons = (item.items || []).filter((i) => i.button);

      // For "Other events", always link to events page (not tournaments page)
      // Only tennis and football have tournaments pages
      const eventsLink = `/events?sport_type=${sportType}`;

      return {
        ...item,
        to: eventsLink, // Always link to events page for "Other events" sports
        items: [
          // First: all tournaments/events items
          ...regularItems,
          // Then: "View all events" button
          {
            label: t("View all events"),
            to: `/events?sport_type=${sportType}`,
            button: true,
          },
          // Keep any existing buttons at the very end
          ...existingButtons,
        ],
      };
    });
    _items = mapped.sort((a, b) => a.label.localeCompare(b.label));
  } else if (props.item.label == "Football") {
    _items = [
      ...Object.entries(countries.value!).map(([key, value]) => {
        return {
          to:
            key == "WORLD"
              ? buildWorldTournamentLink(value.popularTournaments)
              : eventsWithCountry(key.toLowerCase(), { sport_type: "football" }),
          icon:
            key == "WORLD"
              ? h(TrophyIcon, { class: "size-7 p-1.5 shadow-md text-black rounded-full" })
              : h(CountryFlag, { code: key, class: "shadow-md" }),
          label:
            key == "WORLD"
              ? "Other leagues"
              : (() => {
                  const country = isoCountry2.find((x) => x.code3 == key);
                  const regionCode = country?.code || key;
                  let displayName;
                  try {
                    displayName = new Intl.DisplayNames([locale.value.replace("_", "-")], { type: "region" }).of(
                      regionCode
                    );
                  } catch {
                    displayName = new Intl.DisplayNames(["en"], { type: "region" }).of(regionCode);
                  }
                  return displayName || key;
                })(),
          items: [
            (value.popularTeams.length && {
              label: "Popular teams",
              items: value.popularTeams.map((team) => ({
                label: team.officialName,
                to: eventsWithTeam(slugify(team.slug), { sport_type: "football" }),
              })),
              to:
                key == "WORLD"
                  ? eventsWithSportType("football")
                  : eventsWithCountry(key.toLowerCase(), { sport_type: "football" }),
              additionalLabel: (() => {
                // Get all teams for this country - combine popular and non-popular teams (home teams only)
                let allTeamsForCountry = [];
                if (noPopularTeamsCountries.value.includes(key)) {
                  // Country without popular teams - use teamsWithEvents (already filtered to home teams only)
                  allTeamsForCountry = value.allTeams;
                } else {
                  // Country with popular teams - combine popular teams from teamsQuery and home teams from teamsWithEventsForPopularCountries
                  const popularTeams = teamsQuery.data.value?.teams.filter((x) => x.isoCountry == key) || [];
                  const homeTeamsFromQuery =
                    teamsWithEventsForPopularCountries.value.filter((x) => x.isoCountry == key) || [];
                  // Combine and deduplicate by teamId
                  const teamMap = new Map();
                  [...popularTeams, ...homeTeamsFromQuery].forEach((team) => {
                    if (!teamMap.has(team.teamId)) {
                      teamMap.set(team.teamId, team);
                    }
                  });
                  allTeamsForCountry = Array.from(teamMap.values());
                }
                const filteredTeams = allTeamsForCountry.filter((x) => !x.officialName.includes("test"));
                const amount = filteredTeams.length;
                return amount >= 5 ? t("View all {amount} teams", { amount }) : undefined;
              })(),
            }) ||
              (() => {
                const filteredTeams = value.allTeams.filter((x) => !x.officialName.includes("test"));
                // Only show the section if there are teams
                if (filteredTeams.length === 0) return null;
                return {
                  label: "Popular teams",
                  items: filteredTeams
                    .map((team) => ({
                      label: team.officialName,
                      to: `/events?team=${team.slug}&sport_type=football`,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
                  to:
                    key == "WORLD"
                      ? eventsWithSportType("football")
                      : eventsWithCountry(key.toLowerCase(), { sport_type: "football" }),
                  additionalLabel:
                    filteredTeams.length >= 5
                      ? t("View all {amount} teams", { amount: filteredTeams.length })
                      : undefined,
                };
              })(),
            value.popularTournaments.length && {
              label: "Leagues",
              items: value.popularTournaments
                .map(
                  (tournament) =>
                    tournament.number_events && {
                      label: tournament.label,
                      to: { ...tournament.to, query: { ...tournament.to.query, sport_type: "football" } },
                      // Use icon from tournament if available, otherwise check label
                      icon:
                        tournament.icon ||
                        (tournament.label?.toLowerCase().includes("champions league") ||
                        tournament.label?.toLowerCase().includes("europa league") ||
                        tournament.label?.toLowerCase().includes("conference league") ||
                        tournament.label?.toLowerCase().includes("uefa champions") ||
                        tournament.label?.toLowerCase().includes("uefa europa") ||
                        tournament.label?.toLowerCase().includes("uefa conference")
                          ? GlobeAltIcon
                          : undefined),
                      // Preserve ranking for sorting
                      ranking: tournament.ranking || tournament.ranking === 0 ? tournament.ranking : 0,
                    }
                )
                .filter(Boolean)
                .sort((a, b) => {
                  // First: sort by ranking (higher ranking first), if ranking exists
                  const aRanking = a.ranking !== undefined && a.ranking !== null ? a.ranking : 0;
                  const bRanking = b.ranking !== undefined && b.ranking !== null ? b.ranking : 0;
                  if (aRanking !== bRanking) {
                    return bRanking - aRanking; // Higher ranking first
                  }

                  // Second: Special handling: swap Conference League and Europa League
                  const aIsConference = a.label?.toLowerCase().includes("conference league");
                  const bIsConference = b.label?.toLowerCase().includes("conference league");
                  const aIsEuropa = a.label?.toLowerCase().includes("europa league");
                  const bIsEuropa = b.label?.toLowerCase().includes("europa league");

                  // If one is Conference League and the other is Europa League, swap them
                  if (aIsConference && bIsEuropa) return -1; // Conference League comes before Europa League
                  if (aIsEuropa && bIsConference) return 1; // Europa League comes after Conference League

                  // Third: Sort tournaments: non-numbered first (e.g., "Bundesliga"), then numbered (e.g., "2. Bundesliga")
                  const aStartsWithNumber = /^\d/.test(a.label);
                  const bStartsWithNumber = /^\d/.test(b.label);

                  if (aStartsWithNumber && !bStartsWithNumber) return 1;
                  if (!aStartsWithNumber && bStartsWithNumber) return -1;

                  // Finally: If both start with numbers or both don't, sort alphabetically
                  return a.label.localeCompare(b.label);
                }),
            },
          ].filter(Boolean),
        };
      }),
      ...CUSTOM_ISO_COUNTRIES,
      ...(props.item || {}).items.filter((x) => x.label != "Competitions"),
      { label: t("View all football events"), to: eventsWithSportType("football"), button: true },
    ];
  } else if (props.item.label == "Tennis") {
    // Sort the Grand Slams first, then the other tournaments in alphabetical order
    const sr = (arr) => arr.sort((a, b) => a.label.localeCompare(b.label));
    const tournaments = props.item.items[0].items;
    const grandSlamsNames = ["Roland Garros", "Wimbledon", "Australian Open", "US Open"];
    const grandSlams = sr(tournaments.filter((t) => grandSlamsNames.includes(t.label)));
    const others = sr(tournaments.filter((t) => !grandSlams.includes(t)));
    _items = [...grandSlams, ...others];
  } else {
    _items = props.item.items;
  }

  _items = _items?.filter((item) =>
    item.items ? item.items.some((x) => ("number_events" in x ? x.number_events : true)) : true
  );

  if (_items?.length == 1) {
    _items = _items[0].items || _items;
  }

  return _items;
});

const activeItem = ref();

// Keep the displayed side-menu item in sync with the reactive `items` computed.
// `activeItem` holds a snapshot of the hovered country; when `teamsQuery` resolves
// after the hover (cold cache / hard refresh), `items` rebuilds a fresh country
// object with populated `popularTeams`. Re-point `activeItem` at that fresh object
// (matched by label) so the popular teams appear without hovering away and back.
watch(
  items,
  (newItems) => {
    if (!activeItem.value || !newItems?.length) return;
    const match = newItems.find((i) => i.label === activeItem.value.label);
    if (match) activeItem.value = match;
  },
  { immediate: true }
);

const isLoading = computed(() => teamsQuery.isFetching.value);

watch(
  isLoading,
  () => {
    if (activeItem.value || props.mobile || isLoading.value) return;

    activeItem.value = items.value[0];
  },
  { immediate: true }
);
const emit = defineEmits<{ close: [] }>();
</script>
